/**
 * Public landing page at `/`. Layout and copy follow the Claude Design file Scenewise_1; every visual on the
 * page is an interactive Anatomy figure (apps/panel/figures/*, embedded from public/figures):
 *   hero-monitor   hero: a reviewing monitor and its scene strip
 *   label-drawer   "Keywords read the label": an archive drawer, label outside, scenes inside
 *   rule-board     "Write one rule": a switchboard that sorts the scenes
 *   see-*          the four "What it sees" cards
 *   pipeline       "How it works": a three-station line
 * Copy follows the external positioning: no ad platform names, no prices.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { getThemePreference } from '@/lib/theme';
import { FAQ } from './data';
import { Owl } from './Owl';
import './landing.css';

const APP = '/moment/videos';
const PILOT_MAIL = 'mailto:info@skysection.com?subject=Scenewise%20pilot';

type Theme = 'light' | 'dark';

function useReducedMotion() {
  const [reduce, setReduce] = useState(() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduce(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduce;
}

function useInView<T extends Element>(threshold = 0) {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1]!.isIntersecting) setSeen(true);
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, seen };
}

/**
 * One Anatomy figure in an iframe. The figure page posts its height (`{ type: "sw-figure", id, h }`), so the
 * frame grows to fit it; `ratio` reserves space before the first message so nothing jumps.
 */
function Figure({
  name,
  theme,
  title,
  ratio,
  eager = false,
  className,
}: {
  name: string;
  theme: Theme;
  title: string;
  ratio: number;
  eager?: boolean;
  className?: string;
}) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [h, setH] = useState<number | null>(null);
  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      const d = e.data as { type?: string; h?: number };
      if (d?.type === 'sw-figure' && typeof d.h === 'number' && d.h > 40) setH(Math.ceil(d.h));
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, []);
  return (
    <div className={`sw-figure${className ? ` ${className}` : ''}`}>
      <iframe
        ref={frame}
        src={`/figures/${name}${theme === 'light' ? '-light' : ''}.html`}
        title={title}
        loading={eager ? 'eager' : 'lazy'}
        style={h ? { height: h, colorScheme: theme } : { aspectRatio: String(ratio), colorScheme: theme }}
      />
    </div>
  );
}

export function LandingPage() {
  const reduce = useReducedMotion();
  const theme: Theme = getThemePreference() === 'light' ? 'light' : 'dark';

  // the owl in the header looks toward the pointer
  const [pupil, setPupil] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const on = (e: PointerEvent) =>
      setPupil(Math.round(((e.clientX / window.innerWidth) * 2 - 1) * 250) / 100);
    window.addEventListener('pointermove', on);
    return () => window.removeEventListener('pointermove', on);
  }, [reduce]);

  useEffect(() => {
    document.title = 'Scenewise · Every scene, understood';
  }, []);

  return (
    <div className="sw" data-theme={theme}>
      <a className="sw-vh" href="#top">
        Skip to content
      </a>
      <Header pupil={pupil} />
      <main id="top">
        <Hero theme={theme} />
        <Compare theme={theme} />
        <RuleDemo theme={theme} />
        <Stats reduce={reduce} />
        <WhatItSees theme={theme} />
        <HowItWorks theme={theme} />
        <Trust />
        <Faq />
        <Pilot />
      </main>
      <Footer />
    </div>
  );
}

// ---------- header ----------
function Header({ pupil }: { pupil: number }) {
  return (
    <header className="sw-header">
      <div className="sw-wrap sw-header-in">
        <a className="sw-brand" href="#top" aria-label="Scenewise, back to top">
          <Owl className="sw-owl" pupil={pupil} />
          <span>Scenewise</span>
        </a>
        <nav className="sw-nav" aria-label="Main">
          <a href="#what">What it sees</a>
          <a href="#how">How it works</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="sw-actions">
          <Link className="sw-btn sw-btn-quiet" to={APP}>
            Sign in
          </Link>
          <a className="sw-btn sw-btn-primary" href="#pilot">
            Request a pilot
          </a>
        </div>
      </div>
    </header>
  );
}

// ---------- hero ----------
function Hero({ theme }: { theme: Theme }) {
  return (
    <section className="sw-hero" aria-labelledby="sw-hero-title">
      <div className="sw-mesh" aria-hidden="true" />
      <div className="sw-wrap sw-hero-grid">
        <div className="sw-hero-text">
          <h1 id="sw-hero-title" className="sw-h1">
            Every scene, understood.
          </h1>
          <p className="sw-lede">
            Every ad is shown next to a video. Almost none of them know what is happening inside it. Scenewise
            reads the video itself, second by second, and turns what it sees into targeting your campaigns can
            use.
          </p>
          <div className="sw-hero-cta">
            <a className="sw-btn sw-btn-primary" href="#pilot">
              Request a pilot
            </a>
            <a className="sw-btn sw-btn-ghost" href="#how">
              How it works
            </a>
          </div>
        </div>
        <Figure
          name="hero-monitor"
          theme={theme}
          ratio={1408 / 825}
          eager
          className="sw-figure-full"
          title="A reviewing monitor plays a 6:32 menemen recipe video; the scene strip in front of it lights each of its 11 scenes as it plays"
        />
        <p className="sw-caption">
          <strong>6:32 video, 11 scenes, read in 15 s.</strong> Example video. The brands in it are illustrative
          and not Scenewise customers.
        </p>
      </div>
    </section>
  );
}

// ---------- compare ----------
function Compare({ theme }: { theme: Theme }) {
  return (
    <section className="sw-section" id="compare" aria-labelledby="sw-compare-title">
      <div className="sw-wrap">
        <h2 id="sw-compare-title" className="sw-h2">
          Keywords read the label.
          <br />
          Scenewise reads the video.
        </h2>
        <p className="sw-sub">
          The same video, read two ways. The label is all today's targeting knows. Pull the drawer open to see
          what is actually inside.
        </p>
        <Figure
          name="label-drawer"
          theme={theme}
          ratio={900 / 560}
          className="sw-figure-wide sw-figure-full"
          title="An archive drawer: its label shows only the title, channel and keywords; inside stand 11 scene cards, 4 with brands and 1 flagged"
        />
      </div>
    </section>
  );
}

// ---------- write one rule ----------
function RuleDemo({ theme }: { theme: Theme }) {
  return (
    <section className="sw-section" id="rule" aria-labelledby="sw-rule-title">
      <div className="sw-wrap">
        <h2 id="sw-rule-title" className="sw-h2">
          Write one rule.
        </h2>
        <p className="sw-sub">
          Pick the moments your ad belongs next to, and the ones it never should. Flip a switch and every
          scene in the video is checked again.
        </p>
        <Figure
          name="rule-board"
          theme={theme}
          ratio={900 / 600}
          className="sw-figure-wide sw-figure-full"
          title="A switchboard with six rule switches sorts the 11 scenes; with breakfast, family and never accident, 4 scenes qualify"
        />
      </div>
    </section>
  );
}

// ---------- stats ----------
function Stats({ reduce }: { reduce: boolean }) {
  const band = useInView<HTMLElement>(0.35);
  const [c, setC] = useState(reduce ? 1 : 0);
  useEffect(() => {
    if (!band.seen) return;
    if (reduce) {
      setC(1);
      return;
    }
    let raf = 0;
    let t0: number | null = null;
    const step = (ts: number) => {
      if (t0 === null) t0 = ts;
      const p = Math.min(1, (ts - t0) / 1400);
      setC(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [band.seen, reduce]);
  return (
    <section className="sw-stats" ref={band.ref} aria-label="Scenewise in numbers">
      <div className="sw-wrap sw-stats-grid">
        <div className="sw-stat">
          <p className="sw-stat-n">{Math.round(15 * c)} s</p>
          <p className="sw-stat-l">to read a full video</p>
        </div>
        <div className="sw-stat">
          <p className="sw-stat-n">{Math.round(11 * c)}</p>
          <p className="sw-stat-l">scenes in one video, each with brands, mood, topic and transcript</p>
        </div>
        <div className="sw-stat">
          <p className="sw-stat-n">{Math.round(100 * c)}%</p>
          <p className="sw-stat-l">of scenes checked for safety (GARM)</p>
        </div>
      </div>
    </section>
  );
}

// ---------- what it sees ----------
const SEES: Array<{ name: string; title: string; text: string; alt: string }> = [
  {
    name: 'see-brands',
    title: 'Brands on screen or spoken',
    text: 'Logos on packs, shirts and screens, and brand names said out loud, each tied to the second it appears.',
    alt: 'A magnifier settles on the Oreo pack while a microphone listens for brand names',
  },
  {
    name: 'see-mood',
    title: 'Mood, topic and setting',
    text: 'Every scene gets a mood, what it is about and where it happens, so you buy moments instead of whole channels.',
    alt: 'A mood meter swings between negative, neutral and positive, scene by scene',
  },
  {
    name: 'see-safety',
    title: 'Brand safety per scene',
    text: 'Each scene is checked against the GARM categories. One risky minute no longer rules out a whole video, or slips through unseen.',
    alt: 'A film strip advances; at 3:21 a red flag rises and a shutter closes over the flagged frame',
  },
  {
    name: 'see-sources',
    title: 'Any source of video',
    text: 'Channels, playlists, keyword searches, publisher feeds and CTV catalogs, in any language.',
    alt: 'Five different inputs light in turn: channels, playlists, searches, publisher feeds, CTV catalogs',
  },
];

function WhatItSees({ theme }: { theme: Theme }) {
  return (
    <section className="sw-section" id="what" aria-labelledby="sw-what-title">
      <div className="sw-wrap">
        <h2 id="sw-what-title" className="sw-h2">
          What it sees
        </h2>
        <p className="sw-sub">
          Not the title, not the tags. The picture, the sound and the words, scene by scene.
        </p>
        <div className="sw-cards sw-cards-fig">
          {SEES.map((c) => (
            <article key={c.name} className="sw-card sw-card-fig">
              <Figure
                name={c.name}
                theme={theme}
                ratio={360 / 240}
                className="sw-figure-card"
                title={c.alt}
              />
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- how it works ----------
function HowItWorks({ theme }: { theme: Theme }) {
  return (
    <section className="sw-section" id="how" aria-labelledby="sw-how-title">
      <div className="sw-wrap">
        <h2 id="sw-how-title" className="sw-h2">
          How it works
        </h2>
        <ol className="sw-steps">
          <li className="sw-step">
            <span className="sw-step-n">01</span>
            <h3>Point it at content</h3>
            <p>
              Give it channels, playlists, searches or a publisher feed. Scenewise reads every video, scene by
              scene.
            </p>
          </li>
          <li className="sw-step">
            <span className="sw-step-n">02</span>
            <h3>Write one rule</h3>
            <p>Say what your ad should sit next to and what it never should, in plain words.</p>
          </li>
          <li className="sw-step">
            <span className="sw-step-n">03</span>
            <h3>Activate anywhere</h3>
            <p>
              Export the scenes that qualify as placement lists for programmatic platforms, or run an overlay
              that appears at the exact second on partner sites.
            </p>
          </li>
        </ol>
        <Figure
          name="pipeline"
          theme={theme}
          ratio={1000 / 540}
          className="sw-figure-wide sw-figure-full"
          title="A three-station line: intake loads a video, the rule press sorts its scenes, dispatch prints a placement list and shows an overlay at 5:10"
        />
      </div>
    </section>
  );
}

// ---------- trust ----------
function Trust() {
  return (
    <section className="sw-trust" aria-label="Sources and standards">
      <div className="sw-wrap sw-trust-in">
        <div className="sw-trust-g">
          <span className="sw-trust-k">Reads content from</span>
          <b>YouTube</b>
          <b>Publisher feeds</b>
          <b>CTV catalogs</b>
        </div>
        <div className="sw-trust-g">
          <b>GARM brand safety</b>
        </div>
        <div className="sw-trust-g">
          <span className="sw-trust-k">Built in Istanbul by</span>
          <b>Skysection</b>
        </div>
      </div>
    </section>
  );
}

// ---------- faq ----------
function Faq() {
  return (
    <section className="sw-section" id="faq" aria-labelledby="sw-faq-title">
      <div className="sw-wrap sw-faq">
        <div>
          <h2 id="sw-faq-title" className="sw-h2">
            Questions
          </h2>
          <p className="sw-sub">Anything else, ask us during the pilot call.</p>
        </div>
        <div>
          {FAQ.map((item, i) => (
            <details key={item.q} className="sw-qa" open={i === 0}>
              <summary>
                {item.q}
                <span className="sw-qa-ic" aria-hidden="true">
                  <Plus size={16} strokeWidth={2} />
                </span>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- pilot ----------
function Pilot() {
  return (
    <section className="sw-section" id="pilot" aria-labelledby="sw-pilot-title">
      <div className="sw-wrap">
        <div className="sw-cta">
          <div>
            <h2 id="sw-pilot-title" className="sw-h2">
              Start with a pilot campaign.
            </h2>
            <p className="sw-sub">
              Bring one brand and one brief. We read the content, build the segment and hand you the targeting
              within the week.
            </p>
          </div>
          <div className="sw-cta-act">
            <div className="sw-cta-btns">
              <a className="sw-btn sw-btn-primary" href={PILOT_MAIL}>
                Request a pilot
              </a>
              <Link className="sw-btn sw-btn-ghost" to={APP}>
                Sign in
              </Link>
            </div>
            <p className="sw-cta-note">A Skysection product</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- footer ----------
function Footer() {
  return (
    <footer className="sw-footer">
      <div className="sw-wrap">
        <div className="sw-footer-in">
          <div>
            <a className="sw-brand" href="#top" aria-label="Scenewise, back to top">
              <Owl className="sw-owl" />
              <span>Scenewise</span>
            </a>
            <p className="sw-footer-tag">Every scene, understood.</p>
          </div>
          <nav className="sw-footer-links" aria-label="Footer">
            <a href="#what">What it sees</a>
            <a href="#how">How it works</a>
            <a href="#faq">FAQ</a>
            <Link to={APP}>Sign in</Link>
            <a href="#pilot">Request a pilot</a>
          </nav>
        </div>
        <div className="sw-footer-bottom">
          <span>© {new Date().getFullYear()} Scenewise</span>
          <span>A Skysection product, Istanbul</span>
        </div>
        <p className="sw-footer-note">
          The recipe video and every scene on this page are an illustrative example. Danone, Bosch, Lipton and Oreo
          appear only to show brand detection; they are trademarks of their owners and are not affiliated with
          Scenewise.
        </p>
      </div>
    </footer>
  );
}
